import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import newWithSimpleModal from 'Components/reusable/HOC/newWithSimpleModal'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { COUNTRY_CODES } from 'constants/caleo-constants'
import Flag from 'react-world-flags'
import BorderedSection from 'Components/reusable/CaleoCustomComponents/BorderedSection'
import colors from 'constants/colors'
import { useUser } from 'hooks'
import type { CvProgressResponse } from 'types/cvsInterfaces'

/** @notExported */
interface IProfileProgressModalProps {
  /** The backend progress response payload. */
  cv: CvProgressResponse
}

/**
 * The ProfileProgressModal component.
 *
 * @param cv - The CV object.
 * @returns The ProfileProgressModal component.
 * @notExported
 */
const ProfileProgressModal: React.FC<IProfileProgressModalProps> = ({ cv }) => {
  const { t, i18n } = useTranslation()
  const { features } = useUser()

  const languageData = useMemo(() => {
    const byCurrentLanguage = cv.sectionsByLanguage?.[i18n.language]
    if (byCurrentLanguage) {
      return byCurrentLanguage
    }

    for (const language of cv.meta?.supportedLanguages ?? []) {
      const data = cv.sectionsByLanguage?.[language]
      if (data) {
        return data
      }
    }

    const first = Object.values(cv.sectionsByLanguage ?? {})[0]
    return first
  }, [cv, i18n.language])

  const getFlags = (section?: { flags?: string[] }): string[] => {
    if (!section?.flags?.length) {
      return []
    }

    return section.flags
      .map(language => COUNTRY_CODES[language as keyof typeof COUNTRY_CODES])
      .filter((code): code is string => typeof code === 'string' && code.length > 0)
  }

  const renderSectionRow = (label: string, section?: { complete: boolean; flags?: string[] }) => {
    const flags = getFlags(section)
    return (
      <Grid container spacing={1} alignItems="center" direction="row" justifyContent="flex-start">
        <Grid size={1}>{section?.complete ? <CheckIcon color="success" /> : <CloseIcon color="error" />}</Grid>
        <Grid size="auto">{label}</Grid>
        {flags.map(flag => (
          <Grid key={flag} size={1}>
            <Flag
              key={flag}
              sx={{ height: '20px', width: '32px', marginLeft: '10px' }}
              code={flag}
              fallback={<span>{flag}</span>}
            />
          </Grid>
        ))}
      </Grid>
    )
  }

  const roleData = renderSectionRow(t('profile.modal.role'), languageData?.primaryRole)

  const aboutData = renderSectionRow(t('profile.modal.about'), languageData?.about)

  const skillsData = renderSectionRow(t('skills.title'), languageData?.skills)

  const employersData = renderSectionRow(t('employer.title'), languageData?.employers)

  const projectsData = renderSectionRow(t('project.title'), languageData?.projects)

  const educationData = renderSectionRow(t('degrees.title'), languageData?.education)

  const coursesData = renderSectionRow(t('courses.title'), languageData?.courses)

  const certificatesData = renderSectionRow(t('certificates.title'), languageData?.certificates)

  const competencesData =
    !features.includes('competences') || !cv.meta.competencesEnabled
      ? null
      : renderSectionRow(t('competences.title'), languageData?.competences)

  const educationIcon = useMemo(() => {
    if (languageData?.educationGroup.complete) {
      return <CheckIcon color="success" />
    }
    return <CloseIcon color="error" />
  }, [languageData])

  const languagesData = renderSectionRow(t('language.title'), languageData?.languages)

  return (
    <DialogContent>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography gutterBottom fontSize="0.9rem">
            {t('profile.progress.info')}
          </Typography>
          <Typography gutterBottom fontSize="0.9rem">
            {t('profile.progress.info2')}
          </Typography>
        </Grid>
        <Grid size={12}>
          {roleData}
          {aboutData}
          {skillsData}
          {employersData}
          {projectsData}
          <BorderedSection
            borderBefore="1%"
            color={colors.borderColor}
            customIcon={educationIcon}
            title={t('education.title')}
          >
            {educationData}
            {coursesData}
            {certificatesData}
            {competencesData}
          </BorderedSection>
          {languagesData}
        </Grid>
      </Grid>
    </DialogContent>
  )
}

export default newWithSimpleModal(ProfileProgressModal)
